import { useEffect, useMemo } from "react";
import * as THREE from "three";
import type { EdgeState } from "@reagent/sim";
import { PALETTE } from "../palette";
import { useToonMaterial } from "../materials/useToonMaterial";
import { useGlassMaterial } from "../materials/glassMaterial";
import { gridToWorld } from "../iso/gridToWorld";
import { ForecastPin, SegmentBar, WearBar } from "./parts";
import type { ForecastBadge } from "../hazardTells";
import { useGameStore } from "../../app/gameStore";

const PIPE_RADIUS = 0.1;
const PIPE_HEIGHT = 0.14;

const MATERIAL_COLOR: Record<EdgeState["material"], string> = {
  glass: PALETTE.fluidSkyBlue,
  ptfeLined: PALETTE.cream,
  hastelloy: PALETTE.chassisGrey,
};

interface PipeProps {
  edge: EdgeState;
  /** Spec 0018: the earliest crossing the coming turn brings to this run. */
  forecast?: ForecastBadge | null;
}

/**
 * A pipe run between two nodes. Path points come straight from the snapshot
 * so elbows and longer runs need no special-casing here. Corrosion darkens
 * the tube toward terracotta; a burst pipe renders as a broken stub pair.
 */
export function Pipe({ edge, forecast = null }: PipeProps) {
  const ticksPerTurn = useGameStore((s) => s.snapshot.ticksPerTurn);
  const curve = useMemo(() => {
    const points = edge.path.map((p) => {
      const [x, , z] = gridToWorld(p, PIPE_HEIGHT);
      return new THREE.Vector3(x, PIPE_HEIGHT, z);
    });
    return new THREE.CatmullRomCurve3(points, false, "catmullrom", 0);
  }, [edge.path]);

  const geometry = useMemo(
    () => new THREE.TubeGeometry(curve, Math.max(edge.path.length * 8, 16), PIPE_RADIUS, 10, false),
    [curve, edge.path.length],
  );

  // TubeGeometry allocates GPU buffers, and nothing reclaims them when the
  // pipe goes away. Stages are swapped constantly — every batch, every restart,
  // every free-play stage change — so without this a long session leaks a tube
  // per pipe per load until the driver starts refusing allocations.
  useEffect(() => () => geometry.dispose(), [geometry]);

  const wear = edge.maxHp > 0 ? 1 - edge.hp / edge.maxHp : 0;
  const isGlass = edge.material === "glass";

  // Hazard tells mount at the run's midpoint, turned to face along the pipe
  // rather than sitting axis-locked to the world — a gauge collar fitted to
  // an elbowed run still reads as fitted hardware rather than debris.
  const midpoint = useMemo(() => curve.getPointAt(0.5), [curve]);
  const heading = useMemo(() => {
    const tangent = curve.getTangentAt(0.5);
    return Math.atan2(-tangent.z, tangent.x);
  }, [curve]);
  const showPressure = edge.pressureCounter !== 0;
  const showWear = edge.hp !== edge.maxHp;

  const solidMaterial = useToonMaterial(
    wear > 0.6 ? PALETTE.terracottaDark : wear > 0.3 ? PALETTE.terracotta : MATERIAL_COLOR[edge.material],
  );
  const glassMaterial = useGlassMaterial(
    wear > 0.5 ? PALETTE.terracotta : PALETTE.fluidSkyBlue,
    "#FFFFFF",
    0.22 + wear * 0.2,
  );

  if (edge.isBurst) {
    // Burst pipes read as absent flow — a dark, dead stub rather than a tube.
    return (
      <mesh geometry={geometry}>
        <meshToonMaterial color={PALETTE.chassisGreyDark} transparent opacity={0.35} />
      </mesh>
    );
  }

  return (
    <group>
      <mesh geometry={geometry} castShadow receiveShadow>
        <primitive object={isGlass ? glassMaterial : solidMaterial} attach="material" />
      </mesh>
      {edge.isLeaking && <LeakMarker curve={curve} />}
      {/* Edge hazard tells — pressure and wall HP, spec 001 task 5. Mounted at
          the run's midpoint, above and below the tube so a line that is both
          backed up and corroding (a realistic pile-up: pressure builds
          because a downstream buffer is full, wear because what's in it is
          corrosive) shows both without either occluding the other. */}
      {showPressure && (
        <group position={[midpoint.x, PIPE_HEIGHT + 0.16, midpoint.z]} rotation={[0, heading, 0]}>
          <SegmentBar filled={edge.pressureCounter} total={edge.burstLimit} />
        </group>
      )}
      {showWear && (
        <group position={[midpoint.x, 0.05, midpoint.z]} rotation={[0, heading, 0]}>
          <WearBar fraction={edge.maxHp > 0 ? edge.hp / edge.maxHp : 0} />
        </group>
      )}
      {/* The forecast pin clears the pressure collar rather than sharing its
          band: what the line is doing now and what it is about to do are two
          readings, and stacking them would read as one. */}
      {forecast !== null && (
        <ForecastPin
          position={[midpoint.x, PIPE_HEIGHT + 0.48, midpoint.z]}
          tickInTurn={forecast.tickInTurn}
          ticksPerTurn={ticksPerTurn}
        />
      )}
    </group>
  );
}

/** A dribble of escaping fluid at the pipe's midpoint while it leaks. */
function LeakMarker({ curve }: { curve: THREE.CatmullRomCurve3 }) {
  const point = useMemo(() => curve.getPointAt(0.5), [curve]);
  const material = useToonMaterial(PALETTE.fluidMagenta, {
    emissive: PALETTE.fluidMagenta,
    emissiveIntensity: 0.4,
  });
  return (
    <mesh position={[point.x, point.y - 0.05, point.z]}>
      <sphereGeometry args={[0.06, 10, 10]} />
      <primitive object={material} attach="material" />
    </mesh>
  );
}
